import React, { useEffect } from 'react';
import { Box, Typography, TableRow, TableCell, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchDrawingBases, deleteDrawingBase } from 'store/actions/drawingBaseActions';
import { addAlert } from 'store/reducers/alertReducer';
import { usePermissions } from 'hooks/usePermissions';
import { apiCheckDrawingBaseHasDrawings } from 'API/lotteryApi';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { CellTrigger } from 'components/StyledTable/CellTrigger';
import { DrawingBaseInfoPopover } from 'components/DrawingBaseInfoPopover/DrawingBaseInfoPopover';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { DeleteDialog } from 'components/dialog/DeleteDialog';
import RedButton from 'Styled/RedButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { CustomMenu, type CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import type { IDrawingBase } from 'types/IDrawingBase';
import { isDrawingBaseComplete } from 'utility/drawingBaseCompleteness';

const columns: SortableColumn<IDrawingBase>[] = [
  { name: 'Name', width: '60%', sortBy: (base) => base.name },
  { name: 'Ziehungstyp', width: '40%', sortBy: (base) => base.drawingType?.name },
  { name: 'Aktionen', width: '1%' },
];

export const DrawingBasesListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const { drawingBases, loading } = useAppSelector((state) => state.drawingBases);
  const { rows: sortedBases, columns: tableColumns, sort } = useTableSort(columns, drawingBases);
  const { isRedakteur } = usePermissions();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [menuBase, setMenuBase] = React.useState<IDrawingBase | null>(null);
  const [infoAnchor, setInfoAnchor] = React.useState<HTMLElement | null>(null);
  const [infoBase, setInfoBase] = React.useState<IDrawingBase | null>(null);

  useEffect(() => {
    if (lotteryId) {
      dispatch(fetchDrawingBases(Number(lotteryId)));
    }
  }, [dispatch, lotteryId]);

  const handleDeleteClick = async (id: number) => {
    try {
      const { hasDrawings } = await apiCheckDrawingBaseHasDrawings(id);
      if (hasDrawings) {
        dispatch(addAlert({ message: 'Diese Ziehungsbasis kann nicht gelöscht werden, da Ziehungen darauf basieren.', severity: 'warning' }));
        return;
      }
    } catch {
      // proceed with delete attempt if check fails
    }
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  // A base that already has drawings cannot be edited or deleted; only complete bases can start a drawing.
  const menuItemsFor = (base: IDrawingBase): CustomMenuRow[] => {
    if (!isRedakteur()) return [];

    const rows: CustomMenuRow[] = [];
    if (isDrawingBaseComplete(base)) {
      rows.push({ name: 'Neue Ziehung', onClick: () => navigate(`/lottery/${lotteryId}/drawings/create?drawingBaseId=${base.id}`) });
    }
    if (!base.hasDrawings) {
      rows.push(
        { name: 'Editieren', onClick: () => navigate(`/lottery/${lotteryId}/drawing-bases/${base.id}/edit`) },
        { name: 'Löschen', onClick: () => void handleDeleteClick(base.id) }
      );
    }
    return rows;
  };

  const buildMenuItems = (): CustomMenuRow[] => (menuBase ? menuItemsFor(menuBase) : []);

  const openInfo = (base: IDrawingBase) => (anchor: HTMLElement) => {
    setInfoAnchor(anchor);
    setInfoBase(base);
  };

  const handleDelete = async () => {
    if (selectedId) {
      const result = await dispatch(deleteDrawingBase(selectedId));
      if (isFulfilled(result)) {
        setDeleteDialogOpen(false);
        setSelectedId(null);
      }
    }
  };

  return (
    <Box>
      <PageHeader
        title="Ziehungsbasis"
        actions={
          isRedakteur() && (
            <RedButton startIcon={<AddIcon />} onClick={() => navigate(`/lottery/${lotteryId}/drawing-bases/create`)}>
              Neue Ziehungsbasis
            </RedButton>
          )
        }
      />

      {!loading && drawingBases.length === 0 && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          Keine Ziehungsbasis vorhanden.
        </Typography>
      )}

      {drawingBases.length > 0 && (
        <StyledTable columns={tableColumns} sort={sort}>
          {sortedBases.map((base, index) => (
            <TableRow key={base.id} className={`psf-styled-table__body-row ${index === sortedBases.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
              <TableCell>
                <CellTrigger onOpen={openInfo(base)}>{base.name}</CellTrigger>
              </TableCell>
              <TableCell>
                <CellTrigger onOpen={openInfo(base)}>{base.drawingType?.name || '-'}</CellTrigger>
              </TableCell>
              <TableCell>
                <Box className="psf-styled-table__body-cell-action-container">
                  {menuItemsFor(base).length > 0 && (
                    <IconButton
                      className="psf-styled-table__body-cell-action-button"
                      aria-label="Aktionen"
                      onClick={(event) => {
                        setMenuAnchor(event.currentTarget);
                        setMenuBase(base);
                      }}
                    >
                      <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </StyledTable>
      )}

      <DrawingBaseInfoPopover
        drawingBase={infoBase}
        anchorEl={infoAnchor}
        onClose={() => {
          setInfoAnchor(null);
          setInfoBase(null);
        }}
      />

      <CustomMenu
        anchorEl={menuAnchor}
        handleMenuClose={() => {
          setMenuAnchor(null);
          setMenuBase(null);
        }}
        withDivider
        isArrowRight
        items={buildMenuItems()}
      />

      <DeleteDialog open={deleteDialogOpen} title="Ziehungsbasis löschen" onDelete={handleDelete} onCancel={() => setDeleteDialogOpen(false)}>
        <Typography className="psf-dialog-delete__text">Möchten Sie diese Ziehungsbasis wirklich löschen?</Typography>
      </DeleteDialog>
    </Box>
  );
};
